import React, { useState, useEffect } from 'react';
import {
  Card, Button, Table, Modal, Form, Input, Select, Space, Tag,
  Typography, Row, Col, Empty, Tooltip, Upload,
} from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import {
  PlusOutlined, DownloadOutlined, DeleteOutlined, InboxOutlined,
  FileTextOutlined, FilePdfOutlined, LockOutlined, GlobalOutlined,
  BookOutlined, EyeOutlined,
} from '@ant-design/icons';
import { useCondoStore } from '../store/condoStore';
import { useAuthStore } from '../store/authStore';
import { useAuth } from '../../../context/UnifiedAuthContext';
import { CondoDocument } from '../types';
import { downloadAuthed } from '../utils/download';
import { staticApi } from '../utils/staticApi';
import { formatDate } from '../utils/date';
import {
  hasServableFile, toServePath, documentFileState, documentFileTooltip,
} from '../utils/documentFile';

const { Option } = Select;
const { Title, Text } = Typography;
const { Dragger } = Upload;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const CATEGORY_META: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  bylaws:           { label: 'Bylaws',           color: 'blue',   icon: <BookOutlined /> },
  'meeting-minutes':{ label: 'Meeting Minutes',  color: 'green',  icon: <FileTextOutlined /> },
  financial:        { label: 'Financial',        color: 'orange', icon: <FilePdfOutlined /> },
  maintenance:      { label: 'Maintenance',      color: 'red',    icon: <FileTextOutlined /> },
  other:            { label: 'Other',            color: 'default',icon: <FileTextOutlined /> },
};

function categoryTag(category: string) {
  // Normalize first so backend enum names (GOVERNANCE/FINANCE/…) map onto the
  // labelled UI buckets instead of rendering as raw grey tags.
  const key = normalizeCategory(category);
  const meta = CATEGORY_META[key] || { label: category, color: 'default', icon: null };
  return <Tag color={meta.color}>{meta.label}</Tag>;
}

/**
 * True when the document has a file the serve endpoint can actually return.
 *
 * Sprint 147 (ADR-142, R14-7) — this used to be "the string is non-empty",
 * which enabled View for the seeded `/storage/*` rows and then 404'd. The
 * contract now lives in utils/documentFile.ts and is shared with DocumentViewer.
 */
const hasFile = hasServableFile;

/** Derive a sensible download filename from a document's title + stored file. */
function downloadName(doc: CondoDocument): string {
  const ext = (() => {
    const m = (doc.fileUrl || '').match(/\.([a-z0-9]+)(?:$|\?)/i);
    return m ? `.${m[1]}` : '';
  })();
  const base = (doc.title || 'document').replace(/[^\w.\- ]+/g, '').trim() || 'document';
  return base.toLowerCase().endsWith(ext.toLowerCase()) ? base : base + ext;
}

/**
 * Normalize a category value to one of the CATEGORY_META keys so KPI tiles
 * count correctly regardless of how the backend cased / named it.
 *
 * W3 (docs) — the seed/back-end stores categories as enum-ish strings
 * (e.g. "GOVERNANCE", "FINANCE", "MEETING_MINUTES") while the tiles keyed
 * off the lowercase UI slugs ("bylaws", "financial"…). The strict
 * `d.category === key` comparison therefore matched nothing and every tile
 * read 0. Map common backend names onto the UI buckets, falling back to a
 * normalized slug so unknowns at least roll into "other".
 */
function normalizeCategory(raw: string | undefined): string {
  if (!raw) return 'other';
  const slug = raw.trim().toLowerCase().replace(/[\s_]+/g, '-');
  const aliases: Record<string, string> = {
    'bylaws': 'bylaws',
    'governance': 'bylaws',
    'meeting-minutes': 'meeting-minutes',
    'minutes': 'meeting-minutes',
    'financial': 'financial',
    'finance': 'financial',
    'insurance': 'financial',
    'maintenance': 'maintenance',
    'capital-project': 'maintenance',
    'safety': 'maintenance',
  };
  if (aliases[slug]) return aliases[slug];
  return CATEGORY_META[slug] ? slug : 'other';
}

// ─── Component ────────────────────────────────────────────────────────────────

export const Documents: React.FC = () => {
  const { documents, createDocumentApi, uploadDocumentApi, deleteDocumentApi, fetchDocuments } = useCondoStore();
  const { currentCondo, user: storeUser } = useAuthStore();
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const isOwner = user?.role === 'owner' || user?.role === 'OWNER';
  const isAdmin = ['admin', 'ADMIN', 'super_admin_condo', 'SUPER_ADMIN_CONDO', 'superadmin'].includes(user?.role ?? '');

  useEffect(() => {
    fetchDocuments(currentCondo?.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; fetchDocuments is a stable zustand action
  }, [currentCondo?.id]);

  // ── Owners: every public document in this condo ───────────────────────────
  // Sprint 36 — finding H7. Was hard-coded to `category === 'bylaws'`
  // only — i.e. owners saw nothing unless an admin had categorised a
  // document literally "bylaws" (lowercase). Real seed uses
  // GOVERNANCE / FINANCE / INSURANCE / CAPITAL_PROJECT / SAFETY, so
  // residents saw the "No bylaws have been uploaded yet" empty state
  // on a condo with four real public documents. Show everything the
  // admin flagged `isPublic`, regardless of category. The DTO field
  // is `public` (Jackson strips `is`-prefix on booleans), so check
  // either name.
  const visibleDocuments = isOwner
    ? documents.filter(d => (d as any).public === true || (d as any).isPublic === true)
    : documents;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const resetModal = () => {
    setIsModalOpen(false);
    setFileList([]);
    form.resetFields();
  };

  const handleSubmit = async (values: any) => {
    setUploading(true);
    try {
      const hasFile = fileList.length > 0 && fileList[0].originFileObj;

      if (hasFile) {
        // Real file upload via multipart
        const formData = new FormData();
        formData.append('file',        fileList[0].originFileObj as File);
        formData.append('condoId',     currentCondo?.id || '');
        formData.append('title',       values.title);
        formData.append('category',    values.category);
        formData.append('description', values.description || '');
        formData.append('isPublic',    String(values.isPublic ?? true));
        formData.append('uploadedBy',  storeUser?.name || 'admin');

        await uploadDocumentApi(formData);
        staticApi.message.success('Document uploaded successfully');
      } else {
        // No file chosen — save metadata only (placeholder URL)
        await createDocumentApi({
          condoId:    currentCondo?.id || '',
          title:      values.title,
          category:   values.category,
          description:values.description,
          fileUrl:    '',
          uploadedBy: storeUser?.name || 'admin',
          isPublic:   values.isPublic ?? true,
          fileSize:   0,
          uploadDate: new Date(),
        });
        staticApi.message.success('Document record saved (no file attached)');
      }
      resetModal();
    } catch {
      staticApi.message.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (id: string) => {
    staticApi.modal.confirm({
      title:     'Delete this document?',
      content:   'This action cannot be undone.',
      okText:    'Delete',
      okType:    'danger',
      cancelText:'Cancel',
      onOk: async () => {
        try {
          await deleteDocumentApi(id);
          staticApi.message.success('Document deleted');
        } catch {
          staticApi.message.error('Failed to delete document');
        }
      },
    });
  };

  // toServePath now lives in utils/documentFile.ts alongside hasServableFile —
  // the check and the path derivation are two halves of one contract and drifted
  // apart (Sprint 147 / ADR-142).

  /** Honest message for a document whose file we cannot serve. */
  const noFileMessage = (doc: CondoDocument) =>
    documentFileState(doc.fileUrl) === 'unavailable'
      ? `The file for "${doc.title}" is not available on this server`
      : `No file attached to "${doc.title}"`;

  // QA F3 / Sprint 121 — Download via authenticated fetch (a raw anchor would
  // 403 now the token is in-memory). downloadAuthed attaches the bearer, saves
  // the blob, and throws a disambiguated ApiError on failure.
  const handleDownload = async (doc: CondoDocument) => {
    if (!hasFile(doc.fileUrl)) {
      staticApi.message.info(noFileMessage(doc));
      return;
    }
    try {
      await downloadAuthed(toServePath(doc.fileUrl as string), { filename: downloadName(doc) });
    } catch (e: any) {
      staticApi.message.error(`Download failed: ${e?.message || 'unknown error'}`);
    }
  };

  // QA F2 / Sprint 121 — View via authenticated fetch (opening the raw storage
  // URL in a new tab would 403). downloadAuthed opens the blob in a new tab and
  // falls back to a save if the popup is blocked.
  const handleView = async (doc: CondoDocument) => {
    if (!hasFile(doc.fileUrl)) {
      staticApi.message.info(noFileMessage(doc));
      return;
    }
    try {
      await downloadAuthed(toServePath(doc.fileUrl as string), {
        filename: downloadName(doc),
        openInNewTab: true,
      });
    } catch (e: any) {
      staticApi.message.error(`Could not open document: ${e?.message || 'unknown error'}`);
    }
  };

  // ── Columns ───────────────────────────────────────────────────────────────
  const adminColumns = [
    { title: 'Title', dataIndex: 'title', key: 'title',
      render: (v: string) => <Text strong>{v}</Text> },
    { title: 'Category', dataIndex: 'category', key: 'category',
      render: categoryTag },
    { title: 'Description', dataIndex: 'description', key: 'description',
      render: (v: string) => <Text type="secondary">{v}</Text> },
    { title: 'Visibility', dataIndex: 'isPublic', key: 'isPublic',
      render: (v: boolean) => v
        ? <Tag icon={<GlobalOutlined />} color="green">Public</Tag>
        : <Tag icon={<LockOutlined />} color="default">Private</Tag> },
    { title: 'File', key: 'fileUrl',
      render: (_: any, record: CondoDocument) =>
        hasFile(record.fileUrl)
          ? <Tag color="blue" style={{ cursor: 'pointer' }} onClick={() => handleView(record)}>
              Attached
            </Tag>
          : documentFileState(record.fileUrl) === 'unavailable'
            // Sprint 147 (R14-7) — a fileUrl the serve endpoint cannot resolve.
            // Say so rather than claiming "Attached" and 404ing on click.
            ? <Tag color="warning">Unavailable</Tag>
            : <Tag color="default">No file</Tag>,
    },
    { title: 'Uploaded', dataIndex: 'uploadDate', key: 'uploadDate',
      render: (d: Date | string) => formatDate(d) },
    {
      title: 'Actions', key: 'actions',
      render: (_: any, record: CondoDocument) => (
        <Space>
          <Tooltip title={documentFileTooltip(record.fileUrl, 'view')}>
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
              disabled={!hasFile(record.fileUrl)}
            >
              View
            </Button>
          </Tooltip>
          <Tooltip title={documentFileTooltip(record.fileUrl, 'download')}>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record)}
              disabled={!hasFile(record.fileUrl)}
            >
              Download
            </Button>
          </Tooltip>
          <Tooltip title="Delete document">
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>
              Delete
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const ownerColumns = [
    { title: 'Document', dataIndex: 'title', key: 'title',
      render: (v: string) => (
        <Space>
          <div style={{
            width: 32, height: 32, borderRadius: 8, background: '#eff6ff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <BookOutlined style={{ color: '#2563eb', fontSize: 14 }} />
          </div>
          <Text strong>{v}</Text>
        </Space>
      ),
    },
    { title: 'Description', dataIndex: 'description', key: 'description',
      render: (v: string) => <Text type="secondary">{v}</Text> },
    { title: 'Date', dataIndex: 'uploadDate', key: 'uploadDate',
      render: (d: Date | string) => formatDate(d) },
    {
      title: '', key: 'actions', width: 120,
      render: (_: any, record: CondoDocument) => (
        <Tooltip title={documentFileTooltip(record.fileUrl, 'view')}>
          <Button
            size="small"
            type="primary"
            ghost
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            disabled={!hasFile(record.fileUrl)}
            style={{ borderRadius: 6 }}
          >
            View
          </Button>
        </Tooltip>
      ),
    },
  ];

  // ── Owner view ─────────────────────────────────────────────────────────────
  if (isOwner) {
    return (
      <div>
        <div style={{ marginBottom: 28 }}>
          <Row align="middle" gutter={16}>
            <Col>
              <div style={{
                width: 48, height: 48, borderRadius: 12,
                background: 'linear-gradient(135deg, #2563eb, #4f46e5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <BookOutlined style={{ color: '#fff', fontSize: 22 }} />
              </div>
            </Col>
            <Col>
              <Title level={3} style={{ margin: 0 }}>Condo Bylaws</Title>
              <Text type="secondary">
                Official governing documents for {currentCondo?.name || 'your building'}
              </Text>
            </Col>
          </Row>
        </div>

        <div style={{
          background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
          padding: '12px 18px', marginBottom: 24,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <LockOutlined style={{ color: '#2563eb', fontSize: 16 }} />
          <Text style={{ color: '#1e40af', fontSize: 13 }}>
            This section contains official bylaws and governing documents uploaded by your condo management.
            These are read-only for residents.
          </Text>
        </div>

        <Card
          bordered={false}
          style={{ borderRadius: 12, boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}
          bodyStyle={{ padding: 0 }}
        >
          {visibleDocuments.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span>
                  <Text type="secondary">No bylaws have been uploaded yet.</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Contact your property manager if you need access to governing documents.
                  </Text>
                </span>
              }
              style={{ padding: '48px 0' }}
            />
          ) : (
            <Table
              columns={ownerColumns}
              dataSource={visibleDocuments}
              rowKey="id"
              pagination={visibleDocuments.length > 10 ? { pageSize: 10 } : false}
              showHeader
              style={{ borderRadius: 12, overflow: 'hidden' }}
            />
          )}
        </Card>
      </div>
    );
  }

  // ── Admin / Super Admin view ───────────────────────────────────────────────
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <Row align="middle" gutter={16}>
            <Col>
              <div style={{
                width: 48, height: 48, borderRadius: 12,
                background: 'linear-gradient(135deg, #2563eb, #4f46e5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FileTextOutlined style={{ color: '#fff', fontSize: 22 }} />
              </div>
            </Col>
            <Col>
              <Title level={3} style={{ margin: 0 }}>Document Center</Title>
              <Text type="secondary">Manage condo documents and files</Text>
            </Col>
          </Row>
        </div>
        {isAdmin && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => { form.resetFields(); setFileList([]); setIsModalOpen(true); }}
            style={{ borderRadius: 8, height: 38 }}
          >
            Upload Document
          </Button>
        )}
      </div>

      {/* Category summary chips */}
      {/* W3 — counts were always 0: the tiles compared `d.category === key`
          against lowercase UI slugs while the data uses enum-ish names
          (GOVERNANCE / FINANCE / …). Normalize both sides before counting,
          and bucket whatever's listed so the totals reconcile to the table. */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        <Col>
          <Tag color="purple" style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12 }}>
            Total: {documents.length}
          </Tag>
        </Col>
        {Object.entries(CATEGORY_META).map(([key, meta]) => {
          const count = documents.filter(d => normalizeCategory(d.category) === key).length;
          return (
            <Col key={key}>
              <Tag
                icon={meta.icon}
                color={meta.color}
                style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12 }}
              >
                {meta.label}: {count}
              </Tag>
            </Col>
          );
        })}
      </Row>

      <Card
        bordered={false}
        style={{ borderRadius: 12, boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}
        bodyStyle={{ padding: 0 }}
      >
        <Table
          columns={adminColumns}
          dataSource={visibleDocuments}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          style={{ borderRadius: 12, overflow: 'hidden' }}
        />
      </Card>

      {/* Upload modal */}
      <Modal
        title="Upload New Document"
        open={isModalOpen}
        onCancel={resetModal}
        footer={null}
        width={580}
        style={{ borderRadius: 12 }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 16 }}>
          <Form.Item name="title" label="Title"
            rules={[{ required: true, message: 'Please enter document title' }]}>
            <Input placeholder="e.g. 2024 Condo Bylaws" />
          </Form.Item>

          <Form.Item name="category" label="Category"
            rules={[{ required: true, message: 'Please select category' }]}>
            <Select placeholder="Select category">
              <Option value="bylaws">Bylaws</Option>
              <Option value="meeting-minutes">Meeting Minutes</Option>
              <Option value="financial">Financial</Option>
              <Option value="maintenance">Maintenance</Option>
              <Option value="other">Other</Option>
            </Select>
          </Form.Item>

          <Form.Item name="description" label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}>
            <Input.TextArea placeholder="Brief description of this document" rows={2} />
          </Form.Item>

          <Form.Item name="isPublic" label="Visibility" initialValue={true}>
            <Select>
              <Option value={true}>
                <Space><GlobalOutlined style={{ color: '#16a34a' }} /> Public — visible to owners</Space>
              </Option>
              <Option value={false}>
                <Space><LockOutlined style={{ color: '#9ca3af' }} /> Private — admin only</Space>
              </Option>
            </Select>
          </Form.Item>

          {/* File picker */}
          <Form.Item label="File (optional)">
            <Dragger
              fileList={fileList}
              beforeUpload={() => false}  // prevent auto-upload
              onChange={({ fileList: newList }) => setFileList(newList.slice(-1))}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg"
              maxCount={1}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ color: '#6366f1' }} />
              </p>
              <p className="ant-upload-text" style={{ fontSize: 13 }}>
                Click or drag a file to attach
              </p>
              <p className="ant-upload-hint" style={{ fontSize: 11, color: '#aaa' }}>
                PDF, Word, Excel, or image — max 50 MB
              </p>
            </Dragger>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <Button onClick={resetModal}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={uploading}>
                {fileList.length > 0 ? 'Upload & Save' : 'Save Record'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Documents;
